import type { AppliedAllocationChange, AppliedImportChanges } from '@/types/capacityImport';

export type DiffRowStatus = 'increased' | 'decreased' | 'created' | 'kept' | 'unchanged';

export type DiffFilter = 'all' | DiffRowStatus;

export interface ImportDiffRow {
  vendorId: string;
  vendorName: string;
  terminationTypeId: string;
  terminationCode: string;
  weekStart: string;
  action: AppliedAllocationChange['action'];
  status: DiffRowStatus;
  before: number | null;
  after: number | null;
  delta: number | null;
  change: AppliedAllocationChange;
}

export interface ImportDiffVendorGroup {
  vendorId: string;
  vendorName: string;
  rows: ImportDiffRow[];
  net: number;
}

export interface ImportDiffSummary {
  total: number;
  increased: number;
  decreased: number;
  created: number;
  kept: number;
  unchanged: number;
}

// Status reads planningCapacity — the number the table displays — so the red rows and the
// decrease count can never disagree with what is on screen. The backend derives its own
// decreasedMismatches from raw `capacity`; see the design doc's "Known follow-up".
function statusFor(action: AppliedAllocationChange['action'], before: number | null, after: number | null): DiffRowStatus {
  if (action === 'created') return 'created';
  if (action === 'skipped') return 'kept';
  if (before === null || after === null) return 'unchanged';
  if (after < before) return 'decreased';
  if (after > before) return 'increased';
  return 'unchanged';
}

export function toDiffRows(changes: AppliedImportChanges): ImportDiffRow[] {
  return changes.vendors.flatMap((vendor) =>
    vendor.changes.map((change) => {
      const before = change.before?.planningCapacity ?? null;
      const after = change.after?.planningCapacity ?? null;
      return {
        vendorId: vendor.vendorId,
        vendorName: vendor.vendorName,
        terminationTypeId: change.terminationTypeId,
        terminationCode: change.terminationCode,
        weekStart: change.weekStart,
        action: change.action,
        status: statusFor(change.action, before, after),
        before,
        after,
        delta: before !== null && after !== null ? after - before : null,
        change,
      };
    })
  );
}

export function groupByVendor(rows: ImportDiffRow[]): ImportDiffVendorGroup[] {
  const groups = new Map<string, ImportDiffVendorGroup>();
  rows.forEach((row) => {
    const existing = groups.get(row.vendorId);
    const group = existing ?? { vendorId: row.vendorId, vendorName: row.vendorName, rows: [], net: 0 };
    group.rows.push(row);
    group.net += row.delta ?? 0;
    if (!existing) groups.set(row.vendorId, group);
  });
  return [...groups.values()];
}

export function summarize(rows: ImportDiffRow[]): ImportDiffSummary {
  const countOf = (status: DiffRowStatus): number => rows.filter((row) => row.status === status).length;
  return {
    total: rows.length,
    increased: countOf('increased'),
    decreased: countOf('decreased'),
    created: countOf('created'),
    kept: countOf('kept'),
    unchanged: countOf('unchanged'),
  };
}

export function matchesFilter(row: ImportDiffRow, filter: DiffFilter): boolean {
  return filter === 'all' || row.status === filter;
}
